import React from 'react'
import { ScrollView, TextInput, TouchableOpacity, View } from 'react-native'

import { DS } from '@/design-system'
import type { DSIcon } from '@/design-system/Icon'
import { Text } from '@/components/index'

import { startStyles } from './styles'

export type OnboardingQuestionData = {
  question: string
  subtitle?: string
  type: 'single' | 'multiple'
  values: string[]
  footer?: string
}

type OnboardingQuestionProps = {
  question: OnboardingQuestionData
  answers: string[]
  onAnswersChange: (answers: string[]) => void
}

// Anything in `answers` that isn't one of the question's own listed values must be
// the free-typed "Other" text — derived, not tracked separately, so screens only
// ever need to hold one flat answers array (no per-screen "Other" merge logic).
export function OnboardingQuestion({ question, answers, onAnswersChange }: OnboardingQuestionProps) {
  const selectedValues = answers.filter((a) => question.values.includes(a))
  const otherResponse = answers.find((a) => !question.values.includes(a)) ?? ''
  const showOtherInput = selectedValues.includes('Other')

  const onSelectOption = (option: string) => {
    let nextSelected: string[]
    if (question.type === 'single') {
      nextSelected = [option]
    } else {
      const alreadySelected = selectedValues.includes(option)
      nextSelected = alreadySelected ? selectedValues.filter((a) => a !== option) : [...selectedValues, option]
    }
    const nextOther = nextSelected.includes('Other') ? otherResponse : ''
    onAnswersChange(nextOther ? [...nextSelected, nextOther] : nextSelected)
  }

  const onOtherResponseChange = (value: string) => {
    onAnswersChange(value ? [...selectedValues, value] : selectedValues)
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <DS.Frame style={[startStyles.slide, { paddingTop: 0, paddingHorizontal: 0 }]}>
        <DS.Frame>
          <View style={{ paddingTop: 16 }}>
            <DS.Text variant="Heading 2/Regular" textAlign="center">
              Create your profile
            </DS.Text>
          </View>
          <View style={{ paddingTop: 32 }}>
            <DS.Text variant="Heading 3/Regular" color="Brand/Dark Grey" textAlign="center">
              {question.question}
            </DS.Text>
          </View>
          {question.subtitle && (
            <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey" textAlign="center">
              {question.subtitle}
            </DS.Text>
          )}
          {question.type === 'multiple' ? (
            <Text style={[startStyles.multipleSelect, { textAlign: 'center', color: '#68706D', marginBottom: 0 }]}>
              Select all that apply.
            </Text>
          ) : null}
        </DS.Frame>
        <DS.Frame style={startStyles.questionsAndFooter}>
          <DS.Frame style={startStyles.questionOptions}>
            {question.values.map((option, index) => {
              const [checkedIcon, uncheckedIcon] =
                question.type === 'single' ? ['Radio', 'Radio-uncheck'] : ['Checkbox', 'Checkbox-uncheck']
              const isSelected = selectedValues.includes(option)
              const icon = isSelected ? checkedIcon : uncheckedIcon

              return (
                <TouchableOpacity onPress={() => onSelectOption(option)} key={`option_${index}`}>
                  <DS.Frame style={[startStyles.optionView, { borderColor: isSelected ? '#000' : 'lightgray' }]}>
                    <DS.Icon name={icon as DSIcon} size={16} color="Global/Black" />
                    <DS.Text style={startStyles.optionText} variant="Body Large/Regular" numberOfLines={3}>
                      {option}
                    </DS.Text>
                  </DS.Frame>
                </TouchableOpacity>
              )
            })}
            {showOtherInput ? (
              <TextInput
                placeholder="Please specify..."
                placeholderTextColor={'#68706D'}
                style={[startStyles.inputField, { borderColor: 'lightgray', paddingHorizontal: 20 }]}
                allowFontScaling={false}
                autoCapitalize={'words'}
                autoCorrect={false}
                maxLength={50}
                autoComplete="off"
                value={otherResponse}
                onChangeText={onOtherResponseChange}
                autoFocus
              />
            ) : null}
          </DS.Frame>
          <DS.Frame>
            {question.footer && (
              <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey">
                {question.footer}
              </DS.Text>
            )}
          </DS.Frame>
        </DS.Frame>
      </DS.Frame>
    </ScrollView>
  )
}
